import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { FailureMode, type Severity } from '../../../types';
import type { BadgeProps } from '@/components/ui/badge';

// Token-based severity variants: white text on the raw #fbc02d/#f57c00 the chart
// palette uses failed WCAG AA (medium/high). These carry contrast-tuned
// foregrounds, and the label word still names the severity.
const SEVERITY_VARIANT: Record<Severity, BadgeProps['variant']> = {
  critical: 'destructive',
  high: 'warning',
  medium: 'warning',
  low: 'success',
};

interface SelectFailureModesStepProps {
  sortedFMs: FailureMode[];
  selectedFMIds: Set<string>;
  totalCount: number;
  onToggle: (fmId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

/** border-separate keeps the sticky header cells' borders; tr borders would drop. */
const headerCell = 'sticky top-0 z-[1] border-b bg-card px-2 py-2 text-xs leading-[1.5]';
const bodyCell = 'border-b px-2 py-1.5 align-middle';

const SelectFailureModesStep: React.FC<SelectFailureModesStepProps> = ({
  sortedFMs,
  selectedFMIds,
  totalCount,
  onToggle,
  onSelectAll,
  onDeselectAll,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="revy-cjk revy-figure text-sm leading-[1.5] text-muted-foreground">
          {t('drbfm.convert.selectFailureModesDescription', {
            selected: selectedFMIds.size,
            total: totalCount,
          })}
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onSelectAll}>
            <span className="revy-cjk">{t('drbfm.convert.selectAll')}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onDeselectAll}>
            <span className="revy-cjk">{t('drbfm.convert.deselectAll')}</span>
          </Button>
        </div>
      </div>

      {/*
        The scroll container has to be this element: ui/table's <Table> wraps the
        table in its own overflow-auto div, which would become the scrollport and
        leave the sticky header stuck to a box that never scrolls.
      */}
      <div className="max-h-[400px] overflow-auto rounded-md border bg-card">
        <table className="w-full caption-bottom border-separate border-spacing-0 text-sm">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={cn(headerCell, 'w-10')} />
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.table.severity')}
              </TableHead>
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.table.failureMode')}
              </TableHead>
              <TableHead className={cn(headerCell, 'revy-cjk')}>
                {t('drbfm.table.failureCause')}
              </TableHead>
              <TableHead className={headerCell}>RPN</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedFMs.map((fm) => (
              <TableRow
                key={fm.id}
                onClick={() => onToggle(fm.id)}
                className="cursor-pointer border-b-0"
              >
                <TableCell className={cn(bodyCell, 'w-10')}>
                  <Checkbox checked={selectedFMIds.has(fm.id)} />
                </TableCell>
                <TableCell className={bodyCell}>
                  <Badge variant={SEVERITY_VARIANT[fm.severity] ?? 'secondary'} className="text-[0.7rem]">
                    {fm.severity}
                  </Badge>
                </TableCell>
                <TableCell className={cn(bodyCell, 'max-w-[250px]')}>
                  <p
                    className="revy-cjk truncate text-sm leading-[1.5]"
                    title={fm.failureDescription}
                  >
                    {fm.failureDescription}
                  </p>
                </TableCell>
                <TableCell className={cn(bodyCell, 'max-w-[200px]')}>
                  <p
                    className="revy-cjk truncate text-sm leading-[1.5] text-muted-foreground"
                    title={fm.failureCause || '-'}
                  >
                    {fm.failureCause || '-'}
                  </p>
                </TableCell>
                <TableCell className={bodyCell}>
                  <p className="revy-figure text-sm leading-[1.5]">{fm.rpnScore || '-'}</p>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>
    </div>
  );
};

export default SelectFailureModesStep;
